// Third Party Library
import { useEffect, useState } from "react";
import { useDispatch, useSelector, batch } from "react-redux";
import { Tabs, message, Divider, Alert } from "antd";

// Constants Library
import { TEMPLATE } from "../../../../constants";

// Components Library
import { BtnSubmit, ButtonLink, TableList } from "../../../../components";
import { SearchPayroll, PayrollAdvanceCompany } from "./components";
import { sumByKey } from "./components/TablePayroll/functions";

// Handler Library
import { exportHDLR, tableHDLR } from "../../../../handlers";

// Services Library
import { TIME, REDUX as REDUX_UTIL } from "../../../../services/util";

// Redux Actions
import * as REDUX_ACTION from "../../../../services/redux/actions";
import { TableItems } from "./components/TablePayroll/items";

// Styles Library
import styles from "../../../../pages/index.module.css";

const { reduxStoreWrapper } = REDUX_UTIL;
const { parseExportTime } = TIME;

const { PAYROLL_SUMMARY_COL } = TEMPLATE.TABLEDATA.PAYROLL;
const { filterOptions } = TEMPLATE.PAYROLL;
const { BTN_IMG } = TEMPLATE.BTN;

const PayrollList = (props) => {
  const { col } = props;
  const dispatch = useDispatch();

  const payrolls = useSelector(reduxStoreWrapper("payroll.byID")) || {};
  const payrollsCoid = useSelector(reduxStoreWrapper("payroll.byCoid")) || {};
  const user = useSelector(reduxStoreWrapper("user"));
  const company = useSelector(reduxStoreWrapper("company.byID"));
  const employee = useSelector(reduxStoreWrapper("employee.byID"));
  const employeeCoid = useSelector(reduxStoreWrapper("employee.byCoid"));
  const loyalty = useSelector(reduxStoreWrapper("loyalty.byID"));

  const [isView, setIsView] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [tabKey, setTabKey] = useState("all");
  const [pageSize, setPageSize] = useState(10);
  const [payroll, setPayroll] = useState([]);
  const [current, setCurrent] = useState(1);
  const [paginatedData, setPaginatedData] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [filteredData, setFilteredData] = useState([]);
  const [isClicked, setIsClicked] = useState(false);

  const { uroleid, coid } = user;

  // Filtered Data
  const statusText = {
    all: "all",
    ready_for_processing: 1,
    on_hold: 2,
    bank_processing: 3,
    disbursed: 4,
    cancelled: 0,
  };

  useEffect(() => {
    if (uroleid === "A") {
      const updatedPayroll = Object.values(payrolls).map((t) => {
        const cp = company[t.coid];
        const emp = Object.values(employee).find((e) => e.uid === t.uid);
        const ltyid = t.loyaltyTier !== "-" && t.loyaltyTier?.split("#");
        const lty = loyalty[ltyid?.[0]];
        const rhb = t.attachment?.rhb?.csv;
        const rhbFile = rhb?.split("/");
        const rhbName = rhbFile && rhbFile.length > 5 ? rhbFile[5] : "";

        return {
          ...t,
          key: t.prid,
          bankName: t.bank.name,
          bankAcc: t.bank.acc,
          company_name: cp?.name,
          employee_memid: emp?.memid,
          employee_ic: emp?.ic,
          employee_refid: emp?.empRefID,
          loyalty: lty && Object.values(lty).length ? lty : {},
          rhbName: rhbName,
        };
      });
      setPayroll(updatedPayroll);
    } else {
      if (uroleid === "EMR") {
        const updatedPayroll = Object.values(payrollsCoid).map((t) => {
          const emp = Object.values(employeeCoid).find((e) => e.uid === t.uid);
          const ltyid = t.loyaltyTier?.split("#");
          const lty = loyalty[ltyid?.[0]];

          return {
            ...t,
            key: t.prid,
            bankName: t.bank.name,
            bankAcc: t.bank.acc,
            employee_memid: emp?.memid,
            employee_ic: emp?.ic,
            employee_refid: emp?.empRefID,
            tiers: lty?.tiers,
          };
        });
        setPayroll(updatedPayroll);
      }
    }
  }, [payrolls, employee, company, loyalty]);

  useEffect(() => {
    if (uroleid === "A") {
      dispatch(
        REDUX_ACTION.v1_payroll.get_payroll_timeframe_thru_90days_request()
      );
    } else {
      if (uroleid === "EMR") {
        dispatch(
          REDUX_ACTION.v1_payroll.get_payroll_timeframe_by_coid_request(coid)
        );
      }
    }
    return () => {};
  }, [dispatch, uroleid]);

  useEffect(() => {
    if (payroll.length > 0)
      setFilteredData(tableHDLR.dataTableSorting(payroll));
  }, [payroll]);

  const handleTab = (key) => {
    setTabKey(key);
    setCurrent(1);
  };

  // Pagination
  const filteredDataCount = tableHDLR.dataCount(statusText, filteredData);

  useEffect(() => {
    if (filteredData.length) {
      const startIndex = (current - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const newData = tableHDLR.datasourceFilter(filteredData, tabKey) ?? [];
      const slicedData = newData.slice(startIndex, endIndex);

      setPaginatedData(slicedData);
    }
  }, [current, pageSize, filteredData, tabKey]);

  const handlePageChange = (page) => {
    const sorted = tableHDLR.dataTableSorting(page);
    setPaginatedData(sorted);
  };

  // Exports Data
  const exportData = selectedRow.length > 0 ? selectedRow : filteredData;
  const template = PAYROLL_SUMMARY_COL({
    exportState: true,
    redux: {
      user,
    },
  });

  const exportButton = (
    <BtnSubmit
      display="inline-block"
      text="Export"
      bgColor="transparent"
      color="#4CAF50"
      padding="9px 18px"
      onClick={() =>
        exportHDLR.exportWithTemplate({
          data: exportData,
          filename: `PayrollSummary_${parseExportTime()}`,
          template: template,
        })
      }
      showIcon={true}
      icon={BTN_IMG.EXPORT}
      icon_alt={"export"}
    />
  );

  const selectedRequested = (data) => sumByKey(data, "reqAmount");
  const selectedDisburse = (data) => sumByKey(data, "nda");

  const dataProcess = payroll.filter((f) => f.status === 1);
  const companies = dataProcess.reduce((acc, current) => {
    if (!acc.some((item) => item.coid === current.coid)) {
      acc.push(current);
    }
    return acc;
  }, []);
  
  const textProcess = (
    <span style={{ display: "inline-flex" }}>
      You have {companies.length} companies with pending payroll advances for
      processing. &nbsp;
      <ButtonLink onClick={() => setIsView(true)}>View more</ButtonLink>
    </span>
  );

  return (
    <>
      {contextHolder}
      {uroleid === "A" && companies.length ? (<Alert message={textProcess} type="info" style={{ marginTop: "4em" }} />) : (<></>)}
      {selectedRow.length ? (
        <div className={styles.cardSummary} style={{marginTop: companies.length > 0 ? "1em" : "4em" }}>
          <span style={{ marginRight: 7 }}>
            Selected Payroll Advances: {selectedRow.length}
          </span>
          <Divider type="vertical" style={{ borderColor: "gray" }} />
          <span style={{ marginRight: 7 }}>
            Selected Requested Amount: RM {selectedRequested(selectedRow)}
          </span>
          <Divider type="vertical" style={{ borderColor: "gray" }} />
          <span style={{ marginRight: 7 }}>
            Selected Net Disbursement Amount: RM {selectedDisburse(selectedRow)}
          </span>
        </div>
      ) : ( <></>)}
      <div style={{
          width: "80vw",
          marginTop: selectedRow.length > 0 ? "1em" : companies.length > 0 ? "1em" : (uroleid === "A" ? "4.5em" : "") }}>
        <PayrollAdvanceCompany
          isOpen={isView}
          setIsOpen={setIsView}
          companies={companies}
          dataSource={dataProcess}
          filteredData={payroll}
          setFilteredData={setFilteredData}
          setTabKey={setTabKey}
          setIsPending={setIsPending}
        />
        <div>
          <TableList.FilterData
            isModalOpen={isModalOpen}
            setIsModalOpen={() => setIsModalOpen(false)}
            selectOptions={filterOptions}
            filteredData={tableHDLR.datasourceFilter(payroll, tabKey)}
            setFilteredData={setFilteredData}
            tabKey={tabKey}
            statusText={statusText}
            type="payroll"
          />
          <SearchPayroll
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            pageSize={pageSize}
            setPageSize={setPageSize}
            tabKey={tabKey}
            current={current}
            setCurrent={setCurrent}
            handlePaginatedDataChange={handlePageChange}
            filteredDataCount={filteredDataCount}
            data={filteredData}
            user={user}
            selectedRow={selectedRow}
            selectedRowKeys={selectedRowKeys}
            messageApi={messageApi}
            setIsClicked={setIsClicked}
          />
        </div>
        <div style={{ marginTop: uroleid === "A" ? "4.5em" : "" }}>
          <Tabs
            activeKey={tabKey}
            tabBarExtraContent={exportButton}
            onTabClick={handleTab}
            items={TableItems(
              filteredDataCount,
              payroll,
              filteredData,
              paginatedData,
              tabKey,
              setFilteredData,
              selectedRow,
              setSelectedRow,
              selectedRowKeys,
              setSelectedRowKeys,
              isPending,
              setIsPending,
              isClicked,
              setIsClicked
            )}
          />
        </div>
      </div>
    </>
  );
};

export default PayrollList;